import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  CalendarClock,
  CarFront,
  CreditCard,
  Pause,
  Receipt,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { useAuth } from '@/lib/auth-context';
import { formatarData, formatBrl } from '@/lib/format';
import { supabase } from '@/lib/supabase';
import { useRealtimeInvalidate } from '@/lib/use-realtime-invalidate';

export function AssinaturaPage() {
  const { user } = useAuth();

  const ass = useQuery({
    queryKey: ['cliente-assinatura', user?.id],
    queryFn: async () => {
      const { data: cli } = await supabase
        .from('tab_clientes')
        .select('seq_cliente')
        .eq('cod_auth_user', user!.id)
        .single();
      if (!cli) return null;
      const { data, error } = await supabase
        .from('tab_assinaturas')
        .select(
          'seq_assinatura, cod_status, qtd_veiculos_contratados, val_mensalidade_atual, dta_inicio, dta_proximo_vencimento, cod_metodo_pagamento, tab_planos(nom_plano, des_plano)',
        )
        .eq('seq_cliente', cli.seq_cliente)
        .order('dta_inicio', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as {
        seq_assinatura: number;
        cod_status: string;
        qtd_veiculos_contratados: number;
        val_mensalidade_atual: number;
        dta_inicio: string;
        dta_proximo_vencimento: string;
        cod_metodo_pagamento: string;
        tab_planos?: { nom_plano: string; des_plano: string | null };
      } | null;
    },
    enabled: !!user,
  });

  // Realtime: webhook Asaas atualiza assinatura/pagamento sem refresh
  useRealtimeInvalidate('tab_assinaturas', ['cliente-assinatura', user?.id]);
  useRealtimeInvalidate('tab_pagamentos', ['cliente-assinatura', user?.id]);

  if (ass.isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-7 w-40" />
        <Skeleton className="h-64 rounded-3xl" />
        <Skeleton className="h-24 rounded-2xl" />
      </div>
    );
  }

  if (!ass.data) {
    return (
      <div className="p-4">
        <div className="octa-page-header octa-fade-in-up mb-4">
          <h1>Sua assinatura</h1>
          <p>Escolha um plano e comece a economizar nas lavagens.</p>
        </div>
        <EmptyState
          variant="hero"
          icon={Receipt}
          title="Sem assinatura ativa"
          description="Escolha um plano sob medida pro seu uso. Quem assina paga menos por lavagem."
          action={
            <Button asChild size="lg" className="rounded-2xl">
              <Link to="/planos">
                <Sparkles className="mr-1.5 h-4 w-4" />
                Ver planos
              </Link>
            </Button>
          }
        />
      </div>
    );
  }

  const a = ass.data;
  const ativa = a.cod_status === 'ATIVA';

  return (
    <div className="space-y-5 p-4 pb-24">
      {/* Header de página */}
      <div className="octa-page-header octa-fade-in-up">
        <p className="text-overline text-muted-foreground uppercase">Minha conta</p>
        <h1>Assinatura</h1>
      </div>

      {/* Cartão Apple Wallet style — gradient + blobs + número gigante */}
      <Card variant="wallet" className="octa-scale-in p-0 ring-1 ring-white/10">
        {/* Blobs decorativos atrás */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/15 blur-3xl"
        />
        <div
          aria-hidden
          className="bg-white/12 pointer-events-none absolute -bottom-20 -left-12 h-44 w-44 rounded-full blur-2xl"
        />

        <div className="relative z-10 space-y-6 p-6">
          {/* Topo: plano + status */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/85">
                Plano ativo
              </p>
              <h2 className="mt-1 text-2xl font-extrabold leading-tight tracking-tight">
                {a.tab_planos?.nom_plano ?? 'Assinatura'}
              </h2>
              {a.tab_planos?.des_plano && (
                <p className="mt-1 max-w-xs text-xs text-white/85">{a.tab_planos.des_plano}</p>
              )}
            </div>
            <StatusBadge
              variant={ativa ? 'success' : 'warning'}
              dot
              pulse={ativa}
              className="bg-white/20 text-white ring-white/30 backdrop-blur"
            >
              {a.cod_status}
            </StatusBadge>
          </div>

          {/* Mensalidade — número gigante */}
          <div className="space-y-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/85">
              Mensalidade
            </p>
            <div className="flex items-baseline gap-1.5">
              <span className="text-5xl font-extrabold leading-none tracking-tight drop-shadow-sm">
                {formatBrl(a.val_mensalidade_atual)}
              </span>
              <span className="text-sm font-medium text-white/85">/mês</span>
            </div>
          </div>

          {/* Divider perfurado estilo ticket */}
          <div className="relative -mx-6 my-2">
            <div className="border-t-2 border-dashed border-white/30" />
            <div className="bg-background absolute -left-1 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full" />
            <div className="bg-background absolute -right-1 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full" />
          </div>

          {/* Footer: veículos + vencimento */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/85">
                <CarFront className="h-3 w-3" />
                Veículos
              </p>
              <p className="mt-1 text-xl font-bold">
                {a.qtd_veiculos_contratados}
                <span className="ml-1 text-xs font-medium text-white/85">contratados</span>
              </p>
            </div>
            <div>
              <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/85">
                <CalendarClock className="h-3 w-3" />
                Próximo
              </p>
              <p className="mt-1 text-sm font-bold leading-tight">
                {formatarData(a.dta_proximo_vencimento)}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Card metodo de pagamento */}
      <Card variant="elevated" className="octa-fade-in-up" style={{ animationDelay: '0.1s' }}>
        <CardContent className="flex items-center gap-3 p-4">
          <div className="bg-octa-gradient shadow-octa-sm flex h-11 w-11 items-center justify-center rounded-xl text-white">
            <CreditCard className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-overline text-muted-foreground uppercase">Pagamento</p>
            <p className="text-sm font-bold">{a.cod_metodo_pagamento}</p>
          </div>
          <Link
            to="/pagamentos"
            className="text-primary octa-tap hover:bg-primary/5 rounded-lg p-2"
            aria-label="Ver histórico de pagamentos"
          >
            <ArrowRight className="h-4 w-4" />
          </Link>
        </CardContent>
      </Card>

      {/* Início da assinatura */}
      <Card variant="flat" className="octa-fade-in-up" style={{ animationDelay: '0.15s' }}>
        <CardContent className="p-4">
          <p className="text-overline text-muted-foreground uppercase">Membro desde</p>
          <p className="mt-1 text-sm font-bold">{formatarData(a.dta_inicio)}</p>
        </CardContent>
      </Card>

      {/* Ações — outline com depth */}
      <div className="octa-fade-in-up grid grid-cols-2 gap-3" style={{ animationDelay: '0.2s' }}>
        <Button variant="outline" size="lg" className="rounded-2xl">
          <Pause className="mr-1.5 h-4 w-4" />
          Pausar
        </Button>
        <Button
          variant="outline"
          size="lg"
          className="rounded-2xl text-rose-600 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/30"
        >
          <XCircle className="mr-1.5 h-4 w-4" />
          Cancelar
        </Button>
      </div>

      <p className="text-muted-foreground text-center text-xs">
        Pra mudar de plano, fale com o suporte.
      </p>
    </div>
  );
}
